import { useCallback, useRef } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

import { pathsSchema } from "shell/platform/config/paths";
import { loadAmplitude } from "shell/platform/lib/analytics/amplitudeLoader";
import { globalCognitoSignOut } from "shell/platform/lib/cognitoPasswordAuth";
import {
  clearCognitoAuthMode,
  clearStoredAuthProvider,
  isCognitoPasswordMode,
} from "shell/platform/lib/oidcClient";

type Props = {
  idleMinutes?: number;
  isEnabled?: boolean;
};

export const useIdleLogout = ({
  idleMinutes = 15,
  isEnabled = true,
}: Props = {}) => {
  const auth = useAuth();
  const isLoggingOut = useRef(false);
  const navigate = useNavigate();

  const onIdle = useCallback(async () => {
    if (!auth?.isAuthenticated || isLoggingOut.current) return;

    isLoggingOut.current = true;
    try {
      const passwordMode = isCognitoPasswordMode();
      const accessToken = auth.user?.access_token;

      if (passwordMode && accessToken) {
        await globalCognitoSignOut(accessToken);
      }
      await auth?.removeUser?.();
      await auth?.clearStaleState?.();
      const amplitude = await loadAmplitude();
      amplitude?.resetAmplitude();
      if (!passwordMode) {
        await auth?.signoutRedirect?.();
      }
      clearStoredAuthProvider();
      clearCognitoAuthMode();
      if (passwordMode) {
        navigate(pathsSchema.index);
      }
    } finally {
      isLoggingOut.current = false;
    }
  }, [auth, navigate]);

  useIdleTimer({
    onIdle: () => {
      onIdle();
    },
    timeout: idleMinutes * 60 * 1000,
    throttle: 500,
    crossTab: true,
    disabled: !auth?.isAuthenticated || !isEnabled,
  });
};
